import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { ToastService } from '../../services/toast.service';
import { Subject, BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthenticationService } from '../../services/auth.service';
import { OllamaService } from '../../services/ollama.service';
import { RemoteConfigService } from '../../services/remote-config.service';
import { environment } from '../../../../environments/environment';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

export interface ChatMessage {
  id: string;
  message: string;
  isUser: boolean;
  timestamp: Date;
  isTyping?: boolean;
  attachments?: {
    name: string;
    type: string;
    size: number;
  }[];
}

@Component({
  selector: 'app-ollama-assistant',
  standalone: true,
  templateUrl: './ollama-assistant.component.html',
  styleUrls: ['./ollama-assistant.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatTooltipModule,
  ],
})
export class OllamaAssistantComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('chatContent', { static: false }) chatContent!: ElementRef<HTMLElement>;
  @ViewChild('messageInput', { static: false }) messageInput!: ElementRef;
  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef;

  private destroy$ = new Subject<void>();

  // AI Assistant enabled preference
  private readonly AI_ASSISTANT_KEY = 'AI_ASSISTANT_ENABLED';
  isAssistantEnabled = true;
  private userPreference: boolean | null = null;
  private globalAIEnabled = true;

  // Chat state
  messages: ChatMessage[] = [];
  isVisible = false;
  isExpanded = false;
  isTyping = false;
  currentMessage = '';

  // File attachment state
  attachedFiles: File[] = [];
  maxFiles = 10;
  maxFileSize = 50 * 1024 * 1024; // 50MB

  // Assistant state
  isOnline = new BehaviorSubject<boolean>(false);
  assistantName = 'Amber';
  assistantAvatar = 'assets/images/amber-avatar.png';

  // Animation and UI
  private chatAnimation: any;
  isLoading = false;

  // Quick action buttons
  quickActions = [
    { id: 'book-service', label: 'Book a Service', icon: 'calendar_today' },
    { id: 'find-provider', label: 'Find Provider', icon: 'search' },
    { id: 'check-booking', label: 'Check Booking', icon: 'format_list_bulleted' },
    { id: 'get-help', label: 'Get Help', icon: 'help_outline' }
  ];

  constructor(
    private authService: AuthenticationService,
    private toastService: ToastService,
    private ollamaService: OllamaService,
    private remoteConfigService: RemoteConfigService
  ) { }

  ngOnInit() {
    this.loadAssistantPreference();
    this.listenForPreferenceChanges();

    // React to remote config changes — admin can disable AI globally
    this.remoteConfigService?.config$?.pipe(takeUntil(this.destroy$)).subscribe((config: any) => {
      // Environment flag takes highest priority
      if (environment.disableChatBubble) {
        this.isAssistantEnabled = false;
        this.isVisible = false;
        return;
      }
      const remoteEnabled = config?.features?.ai?.enabled ?? true;
      this.globalAIEnabled = remoteEnabled;
      // Admin remote config takes priority. User preference can only further disable.
      if (!remoteEnabled) {
        this.isAssistantEnabled = false;
        this.isVisible = false;
      } else {
        this.isAssistantEnabled = this.userPreference !== false;
      }
    });

    this.initializeAssistant();
    this.subscribeToOllamaStatus();
    this.loadPersistedConversation();
  }

  private loadAssistantPreference(): void {
    // Environment-level kill switch — takes highest priority
    if (environment.disableChatBubble) {
      this.isAssistantEnabled = false;
      this.isVisible = false;
      this.globalAIEnabled = false;
      return;
    }
    const stored = localStorage.getItem(this.AI_ASSISTANT_KEY);
    this.userPreference = stored === null ? null : stored === 'true';
    const remoteEnabled = this.remoteConfigService?.isAIEnabled?.() ?? true;
    this.globalAIEnabled = remoteEnabled;
    // Admin remote config takes priority
    if (!remoteEnabled) {
      this.isAssistantEnabled = false;
      this.isVisible = false;
    } else {
      this.isAssistantEnabled = this.userPreference !== false;
    }
  }

  private listenForPreferenceChanges(): void {
    const handler = (event: StorageEvent) => {
      if (event.key === this.AI_ASSISTANT_KEY) {
        this.userPreference = event.newValue === null ? null : event.newValue === 'true';
        if (this.userPreference === false) {
          this.isAssistantEnabled = false;
          this.isVisible = false;
        } else {
          this.isAssistantEnabled = true;
        }
      }
    };
    window.addEventListener('storage', handler);
    this.destroy$.subscribe(() => window.removeEventListener('storage', handler));
  }

  ngAfterViewInit() {    // ViewChild references are now available
    this.setupInitialState();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.chatAnimation) {
      this.chatAnimation.destroy();
    }
  }

  private async loadPersistedConversation() {
    try {
      const history = await this.ollamaService.loadConversationHistory();
      if (history && history.length > 0) {
        // Convert stored messages back to ChatMessage format
        this.messages = history.map((msg: any) => ({
          id: msg.id || this.generateId(),
          message: msg.message,
          isUser: msg.isUser,
          timestamp: new Date(msg.timestamp)
        }));
        console.log('Loaded persisted conversation history');
      }
    } catch (error) {
      console.warn('Failed to load conversation history:', error);
    }
  }

  private async saveConversationToStorage() {
    try {
      await this.ollamaService.saveConversationHistory(this.messages);
    } catch (error) {
      console.warn('Failed to save conversation history:', error);
    }
  }

  private setupInitialState() {
    // Any additional setup that requires ViewChild references
    console.log('Ollama assistant view initialized');
  }

  private subscribeToOllamaStatus() {
    this.ollamaService.isOnline$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (isOnline) => {
          this.isOnline.next(isOnline);
          if (!isOnline) {
            this.showOfflineMessage();
          }
        },
        error: (error) => {
          console.error('Error in Ollama status subscription:', error);
          this.isOnline.next(false);
        }
      });
  }

  private initializeAssistant() {
    // Add welcome message with error handling
    try {
      this.addMessage({
        id: this.generateId(),
        message: `Hi there! 🤖 I'm ${this.assistantName}, your AI virtual assistant powered by Ollama. I'm here to help you with bookings, finding service providers, and answering any questions about our services. How can I assist you today?`,
        isUser: false,
        timestamp: new Date()
      });
    } catch (error) {
      console.error('Error initializing assistant:', error);
    }
  }


  private showOfflineMessage() {
    this.addMessage({
      id: this.generateId(),
      message: 'I\'m currently offline, but you can still browse services and contact support directly. I\'ll be back online soon! 🔄',
      isUser: false,
      timestamp: new Date()
    });
  }

  toggleChat() {
    this.isVisible = !this.isVisible;

    if (this.isVisible) {
      this.animateIn();
      // Focus on input after animation
      setTimeout(() => {
        if (this.messageInput) {
          this.messageInput.nativeElement.focus();
        }
      }, 300);
    } else {
      this.animateOut();
    }
  }

  expandChat() {
    this.isExpanded = !this.isExpanded;

    if (this.isExpanded) {
      this.animateExpand();
    } else {
      this.animateCollapse();
    }
  }

  private animateIn() {
    // Use CSS classes for animation instead of manual DOM manipulation
    // The animation is handled by CSS transitions in the template
    console.log('Animating chat in');
  }

  private animateOut() {
    // Use CSS classes for animation instead of manual DOM manipulation
    console.log('Animating chat out');
  }

  private animateExpand() {
    // Use CSS classes for animation instead of manual DOM manipulation
    console.log('Animating chat expand');
  }

  private animateCollapse() {
    // Use CSS classes for animation instead of manual DOM manipulation
    console.log('Animating chat collapse');
  }

  async sendMessage() {
    if (!this.currentMessage?.trim() && this.attachedFiles.length === 0) return;

    const userMessage = this.currentMessage.trim();
    const attachments = [...this.attachedFiles];

    this.currentMessage = '';
    this.attachedFiles = [];

    try {
      // Add user message with attachments info
      const attachmentInfo = attachments.map(f => ({
        name: f.name,
        type: f.type,
        size: f.size
      }));

      this.addMessage({
        id: this.generateId(),
        message: userMessage || '📎 Sent files',
        isUser: true,
        timestamp: new Date(),
        attachments: attachmentInfo.length > 0 ? attachmentInfo : undefined
      });

      // Show typing indicator
      this.showTypingIndicator();

      const response = attachments.length > 0
        ? await this.sendMultimodalToOllama(userMessage, attachments)
        : await this.sendToOllama(userMessage);

      this.hideTypingIndicator();

      if (response?.success) {
        this.addMessage({
          id: this.generateId(),
          message: response.data?.response || 'I received your message but couldn\'t generate a response.',
          isUser: false,
          timestamp: new Date()
        });

        // Save conversation to storage
        this.saveConversationToStorage();
      } else {
        this.addErrorMessage('Failed to get response');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      this.hideTypingIndicator();
      this.addErrorMessage('An unexpected error occurred. Please try again.');
    }

    // Auto-scroll to bottom
    this.scrollToBottom();
  }

  private async sendMultimodalToOllama(message: string, files: File[]): Promise<any> {
    const user = this.authService.currentUser;

    const response = await this.ollamaService.sendMultimodalMessage({
      message: message,
      files: files,
      context: {
        userId: user?.id || user?._id,
        userName: user?.displayName || user?.username,
        conversationHistory: this.getConversationHistory()
      }
    });

    return response;
  }

  private async sendToOllama(message: string): Promise<any> {
    const user = this.authService.currentUser;

    const response = await this.ollamaService.sendMessage({
      message: message,
      context: {
        userId: user?.id || user?._id,
        userName: user?.displayName || user?.username,
        conversationHistory: this.getConversationHistory()
      }
    });

    return response;
  }

  private getConversationHistory(): any[] {
    return this.messages
      .filter(msg => !msg.isTyping)
      .slice(-10) // Last 10 messages for context
      .map(msg => ({
        role: msg.isUser ? 'user' : 'assistant',
        content: msg.message
      }));
  }

  handleQuickAction(actionId: string) {
    let message = '';

    switch (actionId) {
      case 'book-service':
        message = 'I need to book a service. Can you help me find available providers?';
        break;
      case 'find-provider':
        message = 'Can you help me find service providers in my area?';
        break;
      case 'check-booking':
        message = 'I want to check on my existing bookings.';
        break;
      case 'get-help':
        message = 'I need help with using the app.';
        break;
    }

    if (message) {
      this.currentMessage = message;
      this.sendMessage();
    }
  }

  private showTypingIndicator() {
    this.isTyping = true;
    const typingMessage: ChatMessage = {
      id: 'typing-indicator',
      message: `${this.assistantName} is typing...`,
      isUser: false,
      timestamp: new Date(),
      isTyping: true
    };
    this.messages.push(typingMessage);
  }

  private hideTypingIndicator() {
    this.isTyping = false;
    this.messages = this.messages.filter(msg => msg.id !== 'typing-indicator');
  }

  private addMessage(message: ChatMessage) {
    this.messages.push(message);

    // Auto-scroll to bottom after DOM update
    this.scrollToBottom();
  }

  private async scrollToBottom() {
    try {
      // Wait for DOM to update
      await new Promise(resolve => setTimeout(resolve, 50));

      if (this.chatContent && this.chatContent.nativeElement) {
        const el = this.chatContent.nativeElement;
        // Smooth scroll to bottom
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      }
    } catch (error) {
      console.warn('Error scrolling to bottom:', error);
    }
  }

  private addErrorMessage(errorMessage?: string) {
    const message = errorMessage || 'Sorry, I\'m having trouble responding right now. Please try again or contact support if the issue persists.';
    this.addMessage({
      id: this.generateId(),
      message: message,
      isUser: false,
      timestamp: new Date()
    });
  }

  clearChat() {
    this.messages = [];
    this.initializeAssistant();
    // Clear persisted conversation
    this.saveConversationToStorage();
  }

  async presentToast(message: string) {
    this.toastService.showInfo(message, { duration: 2000 });
  }

  onKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  // Keyboard event handlers for buttons
  onButtonKeyPress(event: KeyboardEvent, action: string) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      switch (action) {
        case 'expand':
          this.expandChat();
          break;
        case 'clear':
          this.clearChat();
          break;
        case 'close':
          this.toggleChat();
          break;
        case 'send':
          this.sendMessage();
          break;
        case 'backdrop':
          this.expandChat();
          break;
      }
    }
  }

  onQuickActionKeyPress(event: KeyboardEvent, actionId: string) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleQuickAction(actionId);
    }
  }

  onImageError(event: any) {
    event.target.src = 'https://cdn.lucystevens.co.za/empirecharterservices/nakel-services/images/1780841396695.webp';
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // Getter for template
  get isAssistantOnline(): boolean {
    return this.isOnline.value;
  }

  // Template helper methods
  formatMessage(message: string): string {
    // Convert URLs to clickable links
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    let formatted = message.replace(urlRegex, '<a href="$1" target="_blank" rel="noopener">$1</a>');

    // Convert line breaks to <br>
    formatted = formatted.replace(/\n/g, '<br>');

    return formatted;
  }

  formatTime(timestamp: Date): string {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  trackByMessage(_index: number, message: ChatMessage): string {
    return message.id;
  }

  // File attachment methods
  onFileSelected(event: any): void {
    const files: FileList = event.target.files;
    if (!files || files.length === 0) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Check file count limit
      if (this.attachedFiles.length >= this.maxFiles) {
        this.presentToast(`Maximum ${this.maxFiles} files allowed`);
        break;
      }

      // Check file size
      if (file.size > this.maxFileSize) {
        this.presentToast(`File ${file.name} exceeds 50MB limit`);
        continue;
      }

      // Check file type
      const validTypes = ['image/', 'application/pdf', 'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml',
        'text/', 'audio/'];
      const isValid = validTypes.some(type => file.type.startsWith(type));

      if (!isValid) {
        this.presentToast(`File type not supported: ${file.name}`);
        continue;
      }

      this.attachedFiles.push(file);
    }

    // Clear input
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  removeAttachment(index: number): void {
    this.attachedFiles.splice(index, 1);
  }

  getFileIcon(file: File): string {
    if (file.type.startsWith('image/')) return 'image-outline';
    if (file.type.startsWith('audio/')) return 'musical-notes-outline';
    if (file.type.includes('pdf')) return 'document-text-outline';
    if (file.type.includes('word')) return 'document-outline';
    return 'document-outline';
  }

}
